import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UpdateDayScheduleRequest, CreateDayScheduleRequest } from '@aegislab/shared';
import {
  createDaySchedule,
  deleteDaySchedule,
  fetchDaySchedulesByDate,
  fetchDaySchedulesByMonth,
  updateDaySchedule,
} from '../api/dayScheduleApi';
import { dayScheduleKeys, invalidateDayScheduleScopes } from './dayScheduleKeys';

/** 그 달의 일정 — 달력 셀 점을 찍는 데 쓴다. */
export function useDaySchedulesByMonth(month: string | undefined, enabled = true) {
  return useQuery({
    queryKey: dayScheduleKeys.month(month),
    queryFn: () => fetchDaySchedulesByMonth(month as string),
    enabled: enabled && !!month,
    staleTime: 60_000,
  });
}

/** 그 날의 일정 — 서랍이 쓴다. 서랍이 닫혀 있으면 부르지 않는다. */
export function useDaySchedulesByDate(date: string | undefined, enabled = true) {
  return useQuery({
    queryKey: dayScheduleKeys.date(date),
    queryFn: () => fetchDaySchedulesByDate(date as string),
    enabled: enabled && !!date,
    staleTime: 60_000,
  });
}

/**
 * 일정 한 건이 바뀌면 그 날 목록과 그 달의 점이 함께 바뀐다 — 세 뮤테이션 모두 같은 scope 를
 * 선언한다. 갈라 선언하면 하나를 빠뜨렸을 때 달력 점이 조용히 stale 된다.
 */
function useInvalidateSchedules() {
  const queryClient = useQueryClient();
  return () => invalidateDayScheduleScopes(queryClient, ['all']);
}

export function useCreateDaySchedule() {
  const invalidate = useInvalidateSchedules();
  return useMutation({
    mutationFn: (input: CreateDayScheduleRequest) => createDaySchedule(input),
    onSuccess: invalidate,
  });
}

export function useUpdateDaySchedule() {
  const invalidate = useInvalidateSchedules();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateDayScheduleRequest & { id: string }) =>
      updateDaySchedule(id, input),
    onSuccess: invalidate,
  });
}

export function useDeleteDaySchedule() {
  const invalidate = useInvalidateSchedules();
  return useMutation({
    mutationFn: (id: string) => deleteDaySchedule(id),
    onSuccess: invalidate,
  });
}
